/* this is a revised version of the Jobseeker Bulk Upload component which only uses a zip file with cvs as pdf / doc / docx */
import React, { useState, useEffect } from "react";
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Info,
  X,
} from "lucide-react";
import { API_BASE_URL, api } from "./utils/api";
import JobSelectionDropDown from "./components/JobSelectionDropDown";
import Modal from "react-modal";
import JobseekerRegisterForm from "./JobseekerRegisterForm";

const BulkUploadRevised = ({
  jobId,
  onUploadComplete,
  defaultShowInstrctions,
  onJobChange,
  jobs,
  currentView,
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
  });

  const handleCloseModal = () => {
    setModalState({ isOpen: false });
  };

  useEffect(() => {
    if (defaultShowInstrctions !== undefined) {
      setShowInstructions(defaultShowInstrctions);
    }
  }, [defaultShowInstrctions]);

  const handleFileSelect = (selectedFile) => {
    // Convert FileList to array
    const filesArray = Array.from(selectedFile);

    let supportedType = [
      ".doc",
      ".docx",
      ".pdf",
      ".zip",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/pdf",
      "application/zip",
      "application/x-zip-compressed",
    ];

    for (let i = 0; i < filesArray.length; i++) {
      if (!supportedType.includes(filesArray[i].type)) {
        alert("Please select a supported (zip, pdf or document) file");
        return;
      }
    }

    setFile(filesArray);
    setUploadResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files); // Convert to array
    handleFileSelect(droppedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleUpload = async () => {
    // Change: file should now be an array or FileList
    if (!file || file.length === 0 || !jobId) {
      alert("Please select file(s) and ensure job ID is provided");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("jobId", jobId);

    // Append multiple files with the same field name
    if (file instanceof FileList || Array.isArray(file)) {
      // If file is a FileList or array, append each file
      Array.from(file).forEach((singleFile) => {
        formData.append("candidateZip", singleFile);
      });
    } else {
      // Single file fallback
      formData.append("candidateZip", file);
    }

    try {
      const response = await api.post(
        "api/jobseekers/bulk-upload-revised",
        formData,
      );
      const result = response.data;
      setUploadResult(result);

      if (result.success) {
        setFile(null);
        if (onUploadComplete) {
          onUploadComplete(jobId);
        }
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      setUploadResult({
        success: false,
        message,
      });
    } finally {
      setUploading(false);
    }
  };
  const openJobseekerRegistrationForm = () => {
    setModalState({
      isOpen: true,
    });
  };
  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg max-w-screen-xl mx-auto p-6 mt-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h2 className="text-2xl font-bold text-indigo-800 w-full">
            Upload new jobseekers
          </h2>
        </div>
        <JobSelectionDropDown
          selectedJob={jobId}
          onJobChange={onJobChange}
          jobs={jobs}
          currentView={currentView}
          key="bulkupload-job-selection-dropdown"
        />
        <button
          onClick={() => setShowInstructions(!showInstructions)}
          className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        >
          <Info className="h-5 w-5" />
          <span>Instructions</span>
        </button>
      </div>

      {/* Instructions Panel */}
      {showInstructions && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6 relative">
          <button
            onClick={() => setShowInstructions(false)}
            className="absolute top-4 right-4 text-blue-500 hover:text-blue-700"
          >
            <X className="h-5 w-5" />
          </button>

          <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Upload Instructions
          </h3>

          <div className="space-y-4 text-sm text-blue-800">
            <div>
              <h4 className="font-semibold mb-2">Prepare Your ZIP File</h4>
              <ul className="list-disc ml-6 space-y-1">
                <li>Include CV files (PDF, DOC, DOCX) for each candidate</li>
                <li>Maximum file size: 50MB</li>
              </ul>
              <h4 className="font-semibold mb-2 mt-2">
                You can also upload miltiple cv files, supported formats
                are:{" "}
              </h4>
              <ul className="list-disc ml-6 space-y-1">
                <li>PDF, DOC, DOCX</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
            ? "border-blue-500 bg-blue-50"
            : file
              ? "border-green-500 bg-green-50"
              : "border-gray-300 hover:border-gray-400"
          }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          accept=".doc,.docx,.pdf,.zip,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,application/zip,application/x-zip-compressed"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
          multiple
        />

        <div className="space-y-4">
          {file ? (
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
          ) : (
            <Upload className="h-12 w-12 text-gray-400 mx-auto" />
          )}

          {file ? (
            <div>
              <p className="text-lg font-medium text-green-700">
                File Selected
              </p>
              <p className="text-sm text-green-600">
                {file.length} file{file.length > 1 ? "s" : ""} selected
              </p>
              <p className="text-xs text-gray-500">
                Total Size:{" "}
                {(
                  file.reduce((total, f) => total + f.size, 0) /
                  1024 /
                  1024
                ).toFixed(2)}{" "}
                MB
              </p>
            </div>
          ) : (
            <div>
              <p className="text-lg font-medium text-gray-700">
                {dragActive
                  ? "Drop your ZIP file here"
                  : "Choose a (ZIP file / CV file) or drag it here"}
              </p>
              <p className="text-sm text-gray-500">Maximum file size: 50MB</p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Button */}
      <div className="mt-6 flex justify-center">
        <button
          onClick={handleUpload}
          disabled={!file || uploading || !jobId}
          className={`px-8 py-3 rounded-lg font-medium transition-colors ${!file || uploading || !jobId
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
        >
          {uploading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Processing...</span>
            </div>
          ) : (
            "Upload Candidates"
          )}
        </button>
      </div>
      <div className="w-full text-center mt-10">
        <a
          href="#"
          className="text-white px-4 py-3 rounded-lg font-medium transition-colors bg-gray-300 text-gray-500 hover:bg-gray-300"
          onClick={openJobseekerRegistrationForm}
        >
          Add candidate manually
        </a>
      </div>
      {/* Upload Result */}
      {uploadResult && (
        <div
          className={`mt-6 p-4 rounded-lg border ${uploadResult.success
              ? "bg-green-50 border-green-200"
              : "bg-red-50 border-red-200"
            }`}
        >
          <div className="flex items-start space-x-3">
            {uploadResult.success ? (
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
            )}

            <div className="flex-1">
              <h4
                className={`font-medium ${uploadResult.success ? "text-green-800" : "text-red-800"
                  }`}
              >
                {uploadResult.success ? "Upload Successful!" : "Upload Failed"}
              </h4>

              <p
                className={`text-sm mt-1 ${uploadResult.success ? "text-green-700" : "text-red-700"
                  }`}
              >
                {uploadResult.message}
              </p>

              {uploadResult.success && uploadResult.data && (
                <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-900">
                      Total Processed
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      {uploadResult.data.totalProcessed}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-900">Successful</div>
                    <div className="text-2xl font-bold text-green-600">
                      {uploadResult.data.successful.length}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-900">Failed</div>
                    <div className="text-2xl font-bold text-red-600">
                      {uploadResult.data.failed.length}
                    </div>
                  </div>
                </div>
              )}

              {uploadResult.success &&
                uploadResult.data &&
                uploadResult.data.successful.length > 0 && (
                  <div className="mt-4">
                    <h5 className="font-medium text-green-800 mb-2">
                      Successfully Added Candidates:
                    </h5>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {uploadResult.data.successful.map((candidate, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between text-xs bg-white p-2 rounded border"
                        >
                          <span className="font-medium">{candidate.name}</span>
                          <span className="text-gray-600">
                            {candidate.email}
                          </span>
                          <span
                            className={`px-2 py-1 rounded text-xs ${candidate.cvAttached
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                              }`}
                          >
                            {candidate.cvAttached ? "✓ CV" : "⚠ No CV"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {uploadResult.success &&
                uploadResult.data &&
                uploadResult.data.failed.length > 0 && (
                  <div className="mt-4">
                    <h5 className="font-medium text-red-800 mb-2">
                      Failed Candidates:
                    </h5>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {uploadResult.data.failed.map((candidate, index) => (
                        <div
                          key={index}
                          className="text-xs bg-white p-2 rounded border"
                        >
                          <div className="font-medium">
                            {candidate.name} ({candidate.email})
                          </div>
                          <div className="text-red-600 mt-1">
                            {candidate.error}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
      <Modal
        isOpen={modalState.isOpen}
        onRequestClose={handleCloseModal}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-2 sm:mx-4 max-h-[90vh] sm:max-h-[85vh] overflow-y-auto relative"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4"
        ariaHideApp={false}
      >
        <div className="sticky top-0 bg-white z-10 border-b border-gray-200 p-3 sm:p-4 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-semibold"></h2>
          <button
            onClick={handleCloseModal}
            className="text-gray-500 hover:text-gray-700 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>
        <div className="p-3 sm:p-4 md:p-6">
          <JobseekerRegisterForm jobId={jobId} onComplete={onUploadComplete} />
        </div>
      </Modal>
    </div>
  );
};

export default BulkUploadRevised;
